import React, { useState } from 'react';
import { MachineInfo, MachineService, NayaxCapturePolicy } from '../api/api_pb';
import { useClient } from '../connect_api';
import { toast } from 'react-toastify';
import RelativeTime from './RelativeTime';
import { formatCapturePolicy, bigintToTimestamp } from '../utils/nayaxUtils';
import { Loader2, Save, CreditCard } from 'lucide-react';

interface NayaxMachineConfigProps {
    machine: MachineInfo;
    onConfigSaved: () => void;
}

const POLICY_OPTIONS: { value: NayaxCapturePolicy; label: string }[] = [
    { value: NayaxCapturePolicy.IMMEDIATE_ON_DISPENSE, label: 'Immediate on Dispense' },
    { value: NayaxCapturePolicy.IMMEDIATE_ON_RETURN, label: 'Immediate on Return' },
    { value: NayaxCapturePolicy.MANUAL, label: 'Manual' },
    { value: NayaxCapturePolicy.AUTO_ON_RETURN_AFTER_HOURS, label: 'Auto After Return (hours)' },
];

function resolveCapturePolicy(policy: NayaxCapturePolicy): NayaxCapturePolicy {
    return policy === NayaxCapturePolicy.UNSPECIFIED ? NayaxCapturePolicy.IMMEDIATE_ON_DISPENSE : policy;
}

const NayaxMachineConfig: React.FC<NayaxMachineConfigProps> = ({ machine, onConfigSaved }) => {
    const machineClient = useClient(MachineService);
    const [nayaxEnabled, setNayaxEnabled] = useState(machine.nayaxMarshalEnabled);
    const [capturePolicy, setCapturePolicy] = useState<NayaxCapturePolicy>(resolveCapturePolicy(machine.nayaxCapturePolicy));
    const [autoHours, setAutoHours] = useState<number>(machine.nayaxAutoCaptureAfterReturnHours || 0);
    const [saving, setSaving] = useState(false);
    const [savedConfig, setSavedConfig] = useState({
        enabled: machine.nayaxMarshalEnabled,
        policy: machine.nayaxCapturePolicy,
        hours: machine.nayaxAutoCaptureAfterReturnHours,
    });

    const handleSave = async () => {
        const shouldUseAutoHours = nayaxEnabled && capturePolicy === NayaxCapturePolicy.AUTO_ON_RETURN_AFTER_HOURS;
        if (shouldUseAutoHours && (autoHours < 1 || autoHours > 22)) {
            toast.error('Auto capture hours must be between 1 and 22');
            return;
        }

        setSaving(true);
        try {
            await machineClient.updateVendingMachine({
                machineUuid: machine.uuid,
                nayaxMarshalEnabled: nayaxEnabled,
                nayaxCapturePolicy: capturePolicy,
                nayaxAutoCaptureAfterReturnHours: shouldUseAutoHours ? autoHours : 0,
            });
            toast.dismiss();
            toast.success('Nayax configuration saved');
            setSavedConfig({
                enabled: nayaxEnabled,
                policy: capturePolicy,
                hours: shouldUseAutoHours ? autoHours : 0,
            });
            onConfigSaved();
        } catch (err: unknown) {
            toast.dismiss();
            toast.error(err instanceof Error ? err.message : 'Failed to save Nayax configuration');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-6" data-testid="nayax-config-section">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-3 mb-4">
                    <CreditCard className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Payment Terminal & Nayax Configuration</h3>
                </div>

                {/* Terminal Info (read-only) */}
                {machine.paymentDeviceType && (
                    <div className="mb-6" data-testid="terminal-info">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Terminal Information</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <p className="text-xs text-gray-500">Device Type</p>
                                <p className="text-sm font-medium text-gray-900" data-testid="device-type">
                                    {machine.paymentDeviceType}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Serial</p>
                                <p className="text-sm font-medium text-gray-900" data-testid="device-serial">
                                    {machine.paymentDeviceSerial || 'N/A'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Firmware</p>
                                <p className="text-sm font-medium text-gray-900" data-testid="device-firmware">
                                    {machine.paymentDeviceFirmware || 'N/A'}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Last Updated</p>
                                <p className="text-sm font-medium text-gray-900" data-testid="device-updated-at">
                                    {machine.paymentDeviceUpdatedAt ? (
                                        <RelativeTime timestamp={bigintToTimestamp(machine.paymentDeviceUpdatedAt)} />
                                    ) : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Nayax Configuration */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Nayax Capture Settings</h4>
                    <div className="space-y-4">
                        {/* Enable/Disable Toggle */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-900">Nayax Marshal Enabled</p>
                                <p className="text-xs text-gray-500">Enable server-side capture management for this machine</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer" data-testid="nayax-enabled-toggle">
                                <input
                                    type="checkbox"
                                    checked={nayaxEnabled}
                                    onChange={(e) => setNayaxEnabled(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                            </label>
                        </div>

                        {/* Capture Policy Selector */}
                        {nayaxEnabled && (
                            <>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Capture Policy</label>
                                    <select
                                        value={capturePolicy}
                                        onChange={(e) => setCapturePolicy(Number(e.target.value) as NayaxCapturePolicy)}
                                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        data-testid="capture-policy-select"
                                    >
                                        {POLICY_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {capturePolicy === NayaxCapturePolicy.IMMEDIATE_ON_DISPENSE && 'Capture immediately when item is dispensed.'}
                                        {capturePolicy === NayaxCapturePolicy.IMMEDIATE_ON_RETURN && 'Capture immediately when item is returned.'}
                                        {capturePolicy === NayaxCapturePolicy.MANUAL && 'Requires manual capture/void via portal. Email notification sent on dispense.'}
                                        {capturePolicy === NayaxCapturePolicy.AUTO_ON_RETURN_AFTER_HOURS && 'Auto-capture after specified hours from item return.'}
                                    </p>
                                </div>

                                {/* Hours input for AUTO_ON_RETURN_AFTER_HOURS */}
                                {capturePolicy === NayaxCapturePolicy.AUTO_ON_RETURN_AFTER_HOURS && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-900 mb-1">
                                            Hours after return
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={22}
                                            value={autoHours}
                                            onChange={(e) => setAutoHours(parseInt(e.target.value, 10) || 0)}
                                            className="block w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            data-testid="auto-hours-input"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Must be between 1 and 22 hours.
                                        </p>
                                    </div>
                                )}

                                {/* Current config display */}
                                {savedConfig.enabled && (
                                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                                        <p>Current saved config: <strong>{formatCapturePolicy(savedConfig.policy)}</strong></p>
                                        {savedConfig.policy === NayaxCapturePolicy.AUTO_ON_RETURN_AFTER_HOURS && (
                                            <p>Auto capture hours: <strong>{savedConfig.hours}</strong></p>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Save Button */}
                        <div>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="nayax-config-save-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save Nayax Configuration
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NayaxMachineConfig;
